import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

// Material-ui
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Typography from "@material-ui/core/Typography";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";

// Icons
import SearchIcon from "@material-ui/icons/Search";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

//Actions
import { setVirtualKeyboard } from "../../actions";

// Other
import { customTheme } from "../../constants/customTheme";
import { colors } from "../../constants/colors";
import VirtualKeyboard from "../Shared/VirtualKeyboard";
import { getCloudStatusIcon } from "../../constants/utilityFunctions";

const useStyles = makeStyles({
  root: {
    height: "100%",
    width: "100%",
    borderRadius: "0px",
    boxShadow: "unset",
    backgroundColor: (props) => colors[props.theme].cardBkg,
  },
  tableContainer: (props) => ({
    marginTop: "15px",
    height: "calc(100% - 125px)",
    maxHeight: "calc(100% - 125px)",
    "@media only screen and (max-width: 900px)": {
      height: "calc(100% - 185px)",
      maxHeight: "calc(100% - 185px)",
    },
    overflowY: "auto",
    ...customTheme(props.theme).customScrollBar,
  }),
  headerRow: {
    backgroundColor: (props) => colors[props.theme].tableHeader,
    padding: "5px 0px 5px 0px",
  },
  subHeaderRow: {
    backgroundColor: (props) => colors[props.theme].tableHeader,
    padding: "5px 0px 5px 0px",
  },
  participantRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: (props) =>
        props.localState.collapseHover
          ? "unset"
          : colors[props.theme].tableHover,
    },
  },
  dataCell: {
    padding: "5px 0px 5px 0px",
  },
  subDataCell: {
    padding: "5px 0px 5px 0px",
    backgroundColor: (props) => colors[props.theme].tableSubRow,
  },
  collapseIconCell: {
    padding: "5px 0px 5px 10px",
  },
  noData: {
    textAlign: "center",
    marginTop: "50px",
  },
  activeSortIcon: {
    opacity: 1,
  },
  inActiveSortIcon: {
    opacity: 0.2,
  },
  checkBox: {
    minWidth: "50px",
    padding: "0px",
  },
  subTableBox: {
    display: "flex",
    flexDirection: "column",
  },
  buttonPrimary: (props) => ({
    ...customTheme(props.theme).buttonPrimary,
    marginTop: "5px",
    alignSelf: "flex-end",
    padding: "3px 10px",
    textTransform: "none",
  }),
  collapse: {
    backgroundColor: (props) => colors[props.theme].headerBkg,
  },
  searchField: {
    marginTop: "5px",
    marginLeft: "20px",
  },
  collapseButton: customTheme().collapseButton,
});

const ParticipantDataTable = (props) => {
  // Table structure for participants
  const columnParticipants = [
    {
      title: "Participant ID",
      field: "participantId",
      // minWidth: "140px",
      paddingLeft: "20px",
    },
    {
      title: "Other ID",
      field: "otherId",
      // minWidth: "100px",
    },
    {
      title: "Number of Studies",
      field: "numberOfStudies",
      // minWidth: "80px",
    },
    {
      title: "Date of Last Study",
      field: "dateOfLastStudy",
      // minWidth: "140px",
    },
  ];

  // Table structure for participants
  const columnStudiesSubHeaders = [
    {
      title: "Protocol Name",
      field: "protocolName",
      // minWidth: "185px",
      paddingLeft: "20px",
    },
    {
      title: "Date",
      field: "studyDate",
      // minWidth: "120px",
    },
    {
      title: "User",
      field: "username",
      minWidth: "120px",
    },
    {
      title: "Progress",
      field: "progress",
      // minWidth: "90px",
    },

    {
      title: "Status",
      field: "status",
      // minWidth: "70px",
    },
  ];

  const getWindowSize = () => {
    let newDataTableSize = window.innerHeight;
    if (window.innerHeight > 600) {
      newDataTableSize = window.innerHeight - 340; // These numbers are based on loadStudy dialog size
    } else {
      newDataTableSize = window.innerHeight - 240; // These numbers are based on loadStudy dialog size
    }
    return newDataTableSize;
  };

  const selectedObject = () => {
    let selectedObject = {};

    props.data.forEach((participant) => {
      let studies = [];
      participant.studyDetails.forEach((study) => {
        if (props.showRemoteStudies) {
          studies.push(study.id);
        } else {
          if (study.studySource !== "REMOTE") {
            studies.push(study.id);
          }
        }
      });
      selectedObject[participant.participantId] = studies;
    });
    return selectedObject;
  };

  // LOCAL STATE
  const [localState, setLocalState] = useState({
    order: "asc",
    orderBy: null,
    open: false,
    searchField: "",
    windowSize: getWindowSize(),
    data: props.data,
    selectedStudies: selectedObject(),
    collapseHover: false,
  });
  const [keyboardInput, setKeyboardInput] = React.useState({
    inputName: "searchField",
    inputUpdate: { searchField: "" },
  });

  const classes = useStyles({ ...props, localState });
  useEffect(() => {
    // Measure window size and dynamicaly change the size of the data table
    const handleResize = () => {
      setLocalState({ ...localState, windowSize: getWindowSize() });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount and unmount

  useEffect(() => {
    setLocalState({
      ...localState,
      data: props.data,
      selectedStudies: selectedObject(),
    });
  }, [props.data, props.showRemoteStudies]);

  const handleSearch = (event) => {
    let newInput = event.target ? event.target.value : event;
    setLocalState({ ...localState, searchField: newInput });
    setKeyboardInput({
      ...keyboardInput,
      inputUpdate: { searchField: newInput },
    });
  };

  const handleFilter = (data) => {
    // Apply search filed filter
    let filteredData = data;
    if (localState.searchField !== "") {
      filteredData = data.filter((row) => {
        let filterCheck = false;
        columnParticipants.forEach((columnHeader) => {
          if (
            row[columnHeader.field]
              .toString()
              .match(
                new RegExp(
                  localState.searchField.replace(
                    /[-\/\\^$*+?.()|[\]{}]/g,
                    "\\$&"
                  ),
                  "i"
                )
              )
          ) {
            filterCheck = true;
          }
        });
        return filterCheck;
      });
    }
    return filteredData;
  };

  const requestSort = (property) => (event) => {
    const isAsc = localState.orderBy === property && localState.order === "asc";
    setLocalState({
      ...localState,
      order: isAsc ? "desc" : "asc",
      orderBy: property,
    }); //toggle between
  };

  function descendingComparator(a, b, orderBy) {
    if (orderBy === "progress") {
      // Need a special case for the progress because progress can be a number (percentage) or NA
      let numA = a[orderBy];
      let numB = b[orderBy];
      if (a[orderBy] !== "NA") {
        numA = Number(a[orderBy].slice(0, -1));
      } else if (a[orderBy] === "NA") {
        numA = -1;
      }
      if (b[orderBy] !== "NA") {
        numB = Number(b[orderBy].slice(0, -1));
      } else if (b[orderBy] === "NA") {
        numB = -1;
      }
      if (numB < numA) {
        return -1;
      }
      if (numB > numA) {
        return 1;
      }
      return 0;
    } else if (orderBy === "dateOfLastStudy" || orderBy === "studyDate") {
      let dateNumA = Number(
        a[orderBy].split("/")[2] +
          a[orderBy].split("/")[0] +
          a[orderBy].split("/")[1]
      );
      let dateNumB = Number(
        b[orderBy].split("/")[2] +
          b[orderBy].split("/")[0] +
          b[orderBy].split("/")[1]
      );
      if (dateNumB < dateNumA) {
        return -1;
      }
      if (dateNumB > dateNumA) {
        return 1;
      }
      return 0;
    } else {
      if (b[orderBy] < a[orderBy]) {
        return -1;
      }
      if (b[orderBy] > a[orderBy]) {
        return 1;
      }
      return 0;
    }
  }

  function stableSort(array) {
    const stabilizedThis = array.map((el, index) => [el, index]);

    stabilizedThis.sort((a, b) => {
      if (localState.order === "desc") {
        return descendingComparator(a[0], b[0], localState.orderBy);
      } else {
        return -descendingComparator(a[0], b[0], localState.orderBy);
      }
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function handleClickCollapse(e, participantId) {
    e.stopPropagation();
    if (localState.open === participantId) {
      setLocalState({
        ...localState,
        open: null,
      });
    } else if (localState.open !== participantId) {
      setLocalState({
        ...localState,
        open: participantId,
      });
    }
  }

  const handleSelectAllClick = (event, row) => {
    let newSelecteds;
    if (event.target.checked) {
      newSelecteds = {
        ...localState.selectedStudies,
        [row.participantId]: row.studyDetails.map((study) => study.id),
      };
    } else {
      newSelecteds = { ...localState.selectedStudies, [row.participantId]: [] };
    }
    setLocalState({ ...localState, selectedStudies: newSelecteds });
  };

  const handleStudyCheckboxClick = (event, row, studyId) => {
    let newSelecteds;
    if (event.target.checked) {
      newSelecteds = {
        ...localState.selectedStudies,
        [row.participantId]: [
          ...localState.selectedStudies[row.participantId],
          studyId,
        ],
      };
    } else {
      newSelecteds = {
        ...localState.selectedStudies,
        [row.participantId]: localState.selectedStudies[
          row.participantId
        ].filter((id) => {
          return id !== studyId;
        }),
      };
    }
    setLocalState({ ...localState, selectedStudies: newSelecteds });
  };

  return (
    <div style={{ height: "100%" }}>
      {/* SEARCH FIELD */}
      {localState.data.length !== 0 && (
        <TextField
          className={classes.searchField}
          label="Search field"
          onChange={handleSearch}
          value={localState.searchField}
          onFocus={() => {
            props.setVirtualKeyboard(true);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      )}
      {/* DATA TABLE */}
      <Paper className={classes.root}>
        {localState.data.length === 0 ? (
          <Typography className={classes.noData}>No study found</Typography>
        ) : (
          <TableContainer
            className={classes.tableContainer}
            style={{
              maxHeight: localState.windowSize,
              height: localState.windowSize,
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow className={classes.headerRow}>
                  {/* HEADER FOR COLLAPSE KEY */}
                  <TableCell
                    className={classes.headerRow}
                    key="collapseKey"
                    style={{
                      minWidth: "20px",
                      paddingLeft: "20px",
                    }}
                  />

                  {/* MAIN TABLE HEADER */}
                  {columnParticipants.map((column) => (
                    <TableCell
                      className={classes.headerRow}
                      key={column.field}
                      align="left"
                      style={{
                        // minWidth: column.minWidth,
                        // width: "auto",
                        paddingLeft: column.paddingLeft
                          ? column.paddingLeft
                          : "unset",
                      }}
                      sortDirection={
                        localState.orderBy === column.field
                          ? localState.order
                          : false
                      }
                    >
                      <TableSortLabel
                        active={localState.orderBy === column.field}
                        direction={
                          localState.orderBy === column.field
                            ? localState.order
                            : "asc"
                        }
                        onClick={requestSort(column.field)}
                        classes={{
                          // Override icon class based on activation state
                          icon:
                            localState.orderBy === column.field
                              ? classes.activeSortIcon
                              : classes.inActiveSortIcon,
                        }}
                      >
                        {column.title}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* DATA ROWS */}
                {stableSort(handleFilter(localState.data)).map((row, index) => {
                  // Exclude participant that have only remote studies if the remote option is toggled off
                  if (!(!props.showRemoteStudies && row.allRemote)) {
                    return (
                      <React.Fragment key={index}>
                        <TableRow
                          onClick={() => {
                            props.handleClickRow({
                              row: row,
                              selectedStudyIds:
                                localState.selectedStudies[row.participantId],
                            });
                          }}
                          tabIndex={-1}
                          role="button"
                          key={index}
                          className={classes.participantRow}
                        >
                          {/* COLLAPSE KEY */}
                          <TableCell className={classes.collapseIconCell}>
                            <IconButton
                              disableRipple
                              className={classes.collapseButton}
                              onMouseDown={(e) => e.stopPropagation()}
                              onMouseEnter={() =>
                                setLocalState({
                                  ...localState,
                                  collapseHover: true,
                                })
                              }
                              onMouseLeave={() =>
                                setLocalState({
                                  ...localState,
                                  collapseHover: false,
                                })
                              }
                              size="small"
                              onClick={(e) =>
                                handleClickCollapse(e, row.participantId)
                              }
                            >
                              {localState.open === row.participantId ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          </TableCell>

                          {/* DATA VALUE */}
                          {columnParticipants.map((column) => {
                            const value = row[column.field];
                            return (
                              <TableCell
                                className={classes.dataCell}
                                style={{
                                  paddingLeft: "20px",
                                }}
                                key={column.field}
                                align="left"
                              >
                                {value}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        {/* SUB TABLE FOR STUDY INFORMATION */}
                        <TableRow>
                          <TableCell
                            className={classes.dataCell}
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={6}
                          >
                            <Collapse
                              in={localState.open === row.participantId}
                              className={classes.collapse}
                              unmountOnExit
                            >
                              <Box margin={1} className={classes.subTableBox}>
                                <Table stickyHeader size="small">
                                  {/* SUBTABLE HEADER */}
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        className={classes.subHeaderRow}
                                        key={"subheaderCheckbox"}
                                        align="left"
                                      >
                                        <Checkbox
                                          className={classes.checkBox}
                                          color="primary"
                                          indeterminate={
                                            localState.selectedStudies[
                                              row.participantId
                                            ].length > 0 &&
                                            localState.selectedStudies[
                                              row.participantId
                                            ].length < row.studyDetails.length
                                          }
                                          checked={
                                            Object.keys(row.studyDetails)
                                              .length > 0 &&
                                            localState.selectedStudies[
                                              row.participantId
                                            ].length ===
                                              Object.keys(row.studyDetails)
                                                .length
                                          }
                                          onChange={(e) =>
                                            handleSelectAllClick(e, row)
                                          }
                                        />
                                      </TableCell>
                                      {columnStudiesSubHeaders.map(
                                        (subHeader) => (
                                          <TableCell
                                            className={classes.subHeaderRow}
                                            key={"subheader" + subHeader.field}
                                            align="left"
                                            // style={{
                                            //   minWidth: subHeader.minWidth,
                                            // }}
                                          >
                                            {subHeader.title}
                                          </TableCell>
                                        )
                                      )}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {/* SUBTABLE DATA ROWS */}
                                    {row.studyDetails.map((studyRow) => {
                                      if (
                                        !(
                                          !props.showRemoteStudies &&
                                          studyRow.studySource === "REMOTE"
                                        )
                                      ) {
                                        return (
                                          <TableRow
                                            key={"subRow" + studyRow.id}
                                            style={{
                                              backgroundColor: localState.selectedStudies[
                                                row.participantId
                                              ].includes(studyRow.id)
                                                ? colors[props.theme].tableHover
                                                : "unset",
                                            }}
                                          >
                                            <TableCell
                                              className={classes.subDataCell}
                                              key={studyRow.id + "Checkbox"}
                                              align="left"
                                            >
                                              <Checkbox
                                                className={classes.checkBox}
                                                color="primary"
                                                checked={localState.selectedStudies[
                                                  row.participantId
                                                ].includes(studyRow.id)}
                                                onClick={(e) =>
                                                  handleStudyCheckboxClick(
                                                    e,
                                                    row,
                                                    studyRow.id
                                                  )
                                                }
                                              />
                                            </TableCell>
                                            {columnStudiesSubHeaders.map(
                                              (subHeader) => {
                                                let value =
                                                  studyRow[subHeader.field];
                                                if (
                                                  subHeader.field === "status"
                                                ) {
                                                  if (
                                                    studyRow.studySource ===
                                                    "LOCAL"
                                                  ) {
                                                    value = getCloudStatusIcon(
                                                      "LOCAL"
                                                    );
                                                  } else if (
                                                    studyRow.studySource ===
                                                    "REMOTE"
                                                  ) {
                                                    value = getCloudStatusIcon(
                                                      studyRow.status
                                                    );
                                                  } else if (
                                                    studyRow.studySource ===
                                                    "BOTH"
                                                  ) {
                                                    if (
                                                      props.showRemoteStudies
                                                    ) {
                                                      value = getCloudStatusIcon(
                                                        studyRow.remoteCopy
                                                          .status
                                                      );
                                                    } else {
                                                      value = getCloudStatusIcon(
                                                        "LOCAL"
                                                      );
                                                    }
                                                  }
                                                }

                                                return (
                                                  <TableCell
                                                    className={
                                                      classes.subDataCell
                                                    }
                                                    key={subHeader.field}
                                                    align="left"
                                                  >
                                                    {value}
                                                  </TableCell>
                                                );
                                              }
                                            )}
                                          </TableRow>
                                        );
                                      }
                                    })}
                                  </TableBody>
                                </Table>

                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() =>
                                    props.handleClickRow({
                                      row: row,
                                      selectedStudyIds:
                                        localState.selectedStudies[
                                          row.participantId
                                        ],
                                    })
                                  }
                                  disabled={
                                    localState.selectedStudies[
                                      row.participantId
                                    ].length === 0
                                  }
                                  className={classes.buttonPrimary}
                                >
                                  {`Review Selected Studies (${
                                    localState.selectedStudies[
                                      row.participantId
                                    ].length
                                  })`}
                                </Button>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  }
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <VirtualKeyboard
        anchor={"center"}
        keyboardInitialInput={{
          searchField: "",
        }}
        keyboardInputUpdate={keyboardInput.inputUpdate}
        keyboardInputName={keyboardInput.inputName}
        keyboardChange={(e) => handleSearch(e)}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      setVirtualKeyboard,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  return {
    theme: state.utility.theme,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ParticipantDataTable);
